import segment from "./segment";
import {
  parseRuleViewedEventPayload,
  parseReportViewedEventPayload,
  parseSnoozeEquipRuleEventPayload,
  parseAddedActionEventPayload,
  parseActionSavedEventPayload,
  parseAssociateInsightWithActionEventPayload,
  parsePostCommentEventPayload,
  stripSitesQuery,
} from "./eventTrackingHelper";

/**
 * User access events
 */
export const userLogin = () => {
  try {
    segment.track("User - Login");
  } catch (err) {
    console.error(`Segment Error: userLogin - ${err}`);
  }
};

/**
 * Segment.io tracking calls for action events
 */

export const viewedInsightEvent = (ruleGroup) => {
  try {
    segment.track("Viewed Insight", parseRuleViewedEventPayload(ruleGroup));
  } catch (err) {
    console.error(`Segment Error: viewedInsightEvent - ${err}`);
  }
};

export const clickedTrendGraphEvent = (asset, ruleName, isAssociated) => {
  try {
    segment.track(
      "Clicked Link To Trend Graph",
      parseReportViewedEventPayload(asset, ruleName, isAssociated)
    );
  } catch (err) {
    console.error(`Segment Error: clickedTrendGraphEvent - ${err}`);
  }
};

export const snoozeEquipEvent = (
  siteName,
  duration,
  equipRuleId,
  successResult
) => {
  try {
    segment.track(
      "Snoozed Equip",
      parseSnoozeEquipRuleEventPayload(
        siteName,
        duration,
        equipRuleId,
        successResult
      )
    );
  } catch (err) {
    console.error(`Segment Error: snoozeEquipEvent - ${err}`);
  }
};

export const searchedEvent = (/* searchName, searchString */) => {
  // TODO: Reinstate once search tracking behaviour has been finalised.
  // try {
  //   if (searchString) {
  //     segment.track(`Searched ${searchName}`, {
  //       query: searchString
  //     })
  //   }
  // } catch (err) {
  //   console.error(`Segment Error: searchedEvent - ${err}`)
  // }
};

export function siteSelectEvent(selected, userSites = [], userProjects = []) {
  try {
    const siteCount = selected.length;
    const projectIds = selected.map((id) => userSites[id].projectId);
    // Unique project names from selected sites
    const projects = Array.from(
      new Set(projectIds.map((id) => userProjects[id].project_name))
    );
    const projectCount = projects.length;
    segment.track("Updated Site Select", {
      projects,
      projectCount,
      siteCount,
    });
  } catch (err) {
    console.error(`Segment Error: siteSelectEvent - ${err}`);
  }
}

/**
 * FYI: This event currently used for create action in Insights and Add action in Actions scene
 */
export const addedActionEvent = (
  issueKey,
  issue,
  site = null,
  associateResult = null,
  associateError = null
) => {
  try {
    segment.track(
      "Added Action",
      parseAddedActionEventPayload(
        issueKey,
        issue,
        site,
        associateResult,
        associateError
      )
    );
  } catch (err) {
    console.error(`Segment Error: addedActionEvent - ${err}`);
  }
};

export const savedActionEvent = (action, actionId, updatedAction) => {
  try {
    segment.track(
      "Action Saved",
      parseActionSavedEventPayload(action, actionId, updatedAction)
    );
  } catch (err) {
    console.error(`Segment Error: savedActionEvent - ${err}`);
  }
};

export const associatedInsightWithActionEvent = (betaIssues, successResult) => {
  try {
    segment.track(
      "Associated Insight With Action",
      parseAssociateInsightWithActionEventPayload(betaIssues, successResult)
    );
  } catch (err) {
    console.error(`Segment Error: associatedInsightWithActionEvent - ${err}`);
  }
};

export const messagePostedEvent = (issue, commentResult) => {
  try {
    segment.track(
      "Message Posted",
      parsePostCommentEventPayload(issue, commentResult)
    );
  } catch (err) {
    console.error(`Segment Error: messagePostedEvent - ${err}`);
  }
};

export const expandAllEvent = (expanded) => {
  try {
    segment.track("Expanded/Collapsed Monitoring List", {
      intent: expanded ? "expand" : "collapse",
    });
  } catch (err) {
    console.error(`Segment Error: expandAllEvent - ${err}`);
  }
};

export const routeChangeEvent = ({ search: rawSearch, pathname }) => {
  try {
    const search = stripSitesQuery(rawSearch);
    segment.track("Page Viewed", {
      search,
      pathname,
    });
  } catch (err) {
    console.error(`Segment Error: expandAllEvent - ${err}`);
  }
};

/**
 * * PERFORMANCE REPORT TRACKING
 */

// * Track when the user selects a new time frame for workflow and comfort reports
export const timeFrameEvent = ({ timeFrameOption }, eventName) => {
  const timeFrame =
    timeFrameOption === 0
      ? "current month"
      : `last ${timeFrameOption} month(s)`;

  try {
    segment.track(eventName, {
      timeFrame,
    });
  } catch (err) {
    console.error(`Segment Error: timeFrameEvent - ${err}`);
  }
};

// * Track when the user changes datagrid sort
export const sortEvent = (
  { chartTitle, columnTitle, direction },
  eventName
) => {
  try {
    segment.track(eventName, {
      columnTitle: columnTitle.toLowerCase(),
      direction: direction.toLowerCase(),
      chartTitle: chartTitle.toLowerCase(),
    });
  } catch (err) {
    console.error(`Segment Error: sortEvent - ${err}`);
  }
};

// * Track when user changes chart Group By option
export const groupByEvent = ({ chartTitle, chartGrouping }, eventName) => {
  try {
    segment.track(eventName, {
      chartTitle: chartTitle.toLowerCase(),
      chartGrouping: chartGrouping.toLowerCase(),
    });
  } catch (err) {
    console.error(`Segment Error: groupByEvent - ${err}`);
  }
};

// * Track when user changes chart Order By option
export const orderByEvent = ({ chartTitle, chartOrder }, eventName) => {
  try {
    segment.track(eventName, {
      chartTitle: chartTitle.toLowerCase(),
      chartOrder: chartOrder.toLowerCase(),
    });
  } catch (err) {
    console.error(`Segment Error: orderByEvent - ${err}`);
  }
};
/**
 * @param {any} d
 * @param {SingleBar[]} bars
 * @param {Sorting} sortDir
 */
export const chartClickEvent = (
  { chartTitle, d, bars, sortDir },
  eventName
) => {
  const bar = bars[d.index];
  const barsCount = bars.length;
  const chartOrder = sortDir === "asc" ? "least to most" : "most to least";
  const barRankPosition = d.index + 1;
  const first = bars[0].value;
  const last = bars[barsCount - 1].value;

  try {
    segment.track(eventName, {
      chartTitle: chartTitle.toLowerCase(),
      chartOrder,
      barId: bar.id.toLowerCase(),
      barLabel: bar.label.toLowerCase(),
      barValue: bar.value,
      barRankPosition,
      barsCount,
      barsValuesMin: sortDir === "asc" ? first : last,
      barsValuesMax: sortDir === "asc" ? last : first,
    });
  } catch (err) {
    console.error(`Segment Error: chartClickEvent - ${err}`);
  }
};

/**
 * @param {any} d
 * @param {SingleBar[]} bars
 * @param {Sorting} sortDir
 */
export const zoneChartClickEvent = (
  { chartTitle, d, bars, sortDir },
  eventName
) => {
  const bar = bars[d.index];
  const barsCount = bars.length;
  const chartOrder = sortDir === "worst" ? "least to most" : "most to least";
  const barRankPosition = d.index + 1;
  const first = bars[0].value;
  const last = bars[barsCount - 1].value;

  try {
    segment.track(eventName, {
      chartTitle: chartTitle.toLowerCase(),
      chartOrder,
      barId: d.categoryLabel.toLowerCase(),
      barLabel: d.categoryLabel.toLowerCase(),
      barValue: bar.value,
      barRankPosition,
      barsCount,
      barsValuesMin: sortDir === "asc" ? first : last,
      barsValuesMax: sortDir === "asc" ? last : first,
    });
  } catch (err) {
    console.error(`Segment Error: zoneChartClickEvent - ${err}`);
  }
};

export const standardRangeClickEvent = (
  { chartTitle, chartDefinedRange },
  eventName
) => {
  try {
    segment.track(eventName, {
      chartTitle: chartTitle.toLowerCase(),
      chartDefinedRange: chartDefinedRange.toLowerCase(),
    });
  } catch (err) {
    console.error(`Segment Error: standardRangeClickEvent - ${err}`);
  }
};
